<template>
  <div class="research-page">
    <header class="research-heading">
      <h1 ref="researchTop" class="research-title" data-scroll-reveal>
        Research
      </h1>
    </header>
    <article data-scroll-group data-scroll-step="110">
      <div ref="researchIntro" class="research-intro" :class="{ 'is-expanded': introExpanded }" data-scroll-ignore>
        <div class="research-summary">
          <p><span class="research-summary-lead">I study how people and AI systems learn from experience, represent knowledge and use it to think and act.</span> <span class="research-summary-rest">As AI becomes part of everyday thinking, I investigate how it shapes the understanding, judgement and abilities people develop.</span></p>
        </div>
        <div id="research-intro-details" v-show="introExpanded" class="research-details">
          <div class="research-detail-column research-detail-left">
            <p>I study how people learn from experience and use what they know. Through experiments and computational models, I examine how internal representations develop and change, and how they support memory, concepts, generalisation, reasoning and action. I build models to develop explanations of these processes and derive predictions for behavioural experiments. I pursue part of this work with Dr. Stephen M. K. Lee at the University of Hong Kong's <a href="https://slrlab.edu.hku.hk/" target="_blank" rel="noopener">Speech, Language, and Reading Lab</a>.</p>
            <p>For AI systems, I study what they learn during training and how it shapes their behaviour. I use behavioural tests and causal interventions to examine their internal representations, how they use information during a task, and how their computations produce the capabilities we observe.</p>
          </div>
          <div class="research-detail-column research-detail-right">
            <p>In human-AI interaction, I study how AI's involvement in everyday life shapes people's cognition and behaviour. This includes how people learn, direct their attention, remember and make decisions, and how their abilities develop through interaction. I am also interested in how people adapt AI to their own purposes, and how those purposes change as they use it.</p>
            <p class="research-connection">I’m interested in how research on human cognition can inspire new approaches to AI, and how building and studying AI can deepen our understanding of the mind. I hope to draw on both to design new ways for people to think, create and act with AI.</p>
          </div>
        </div>
        <button
          class="research-more"
          type="button"
          aria-controls="research-intro-details"
          :aria-expanded="introControlExpanded.toString()"
          @click="toggleIntro"
        >
          <span>{{ introControlExpanded ? 'Show less' : 'More about my research' }}</span>
          <SiteIcon :name="introControlExpanded ? 'minus' : 'plus'" aria-hidden="true" />
        </button>
        <figure class="research-hero-crop">
          <img src="/assets/img/research.png" alt="A person and a computer connected through a shared field of notes and representations" class="research-hero-image">
        </figure>
      </div>

      <div ref="researchCollection" class="research-collection" data-scroll-ignore>
      <div v-if="!isMobileCollection" class="timeline-tools collection-tools" data-scroll-reveal>
        <ViewModeControl
          :model-value="viewMode"
          label="Choose a research view"
          @update:model-value="setViewMode"
        />
        <label class="timeline-search" aria-label="Search research">
          <input
            v-model="searchQuery"
            class="timeline-search-input"
            type="search"
            autocomplete="off"
            spellcheck="false"
            placeholder="Search research"
            @keydown.esc.prevent="clearSearch"
          >
          <i class="fa fa-search timeline-search-icon" aria-hidden="true"></i>
        </label>
      </div>
      <div
        class="collection-view-stage research-collection-view-stage"
        :class="{ 'is-view-transitioning': viewTransitionActive }"
      >
      <SectionMenu v-if="viewMode === 'expanded' && !isMobileCollection" :groups="chapterItems" :children="menuChildren" label="Research sections" />

      <ul v-if="viewMode === 'expanded'" class="timeline" data-scroll-group data-scroll-step="160">
        <template v-for="(pub, entryIndex) in visiblePublications" :key="pub.id">
          <li
            v-if="isGroupStart(pub, entryIndex)"
            :id="chapterId(pub.group)"
            class="timeline-group-anchor"
            aria-hidden="true"
          ></li>
          <li :id="pub.anchor || pub.id" class="timeline-event">
            <div class="timeline-event-copy">
              <FeaturedPublication :entry="compactEntries.find(entry => entry.id === pub.id)" />
            </div>
          </li>
        </template>
      </ul>
      <CollectionBrowser
        v-else
        :entries="compactEntries"
        :mode="collectionMode"
        :featured-count="selectedResearchCount"
        :search-active="searchActive"
        featured-label="Selected"
        archive-label="All"
        :target-id="pendingRouteTargetId"
        @target-consumed="clearPendingRouteTarget"
      />
      <p v-if="searchActive && !hasSearchMatches" class="timeline-empty">No matching research entries.</p>
      </div>
      </div>
    </article>
  </div>
</template>

<script>
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import { publicationDescriptions } from '../data/publications';
import { researchPublications, selectedResearchCount } from '../data/researchPublications';
import { highlightText, highlightHtml } from '../utils/highlight';
import SectionMenu from './SectionMenu.vue';
import FeaturedPublication from './ui/FeaturedPublication.vue';
import ViewModeControl from './ui/ViewModeControl.vue';
import CollectionBrowser from './ui/CollectionBrowser.vue';
import { acquireInteractiveCursorLock } from '../utils/interactiveCursorLock';

export default {
  name: 'ResearchTab',
  components: { SectionMenu, FeaturedPublication, ViewModeControl, CollectionBrowser },
  mixins: [scrollRevealMixin],
  data() {
    return {
      selectedResearchCount,
      introExpanded: false,
      introTarget: null,
      wideIntro: false,
      searchQuery: '',
      viewMode: 'grid',
      viewTransitionActive: false,
      isMobileCollection: typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches,
      pendingRouteTargetId: '',
      pubDesc: publicationDescriptions
    };
  },
  computed: {
    // The control reflects the visitor's request while layout measurement and
    // motion temporarily keep the detail content in its previous state.
    introControlExpanded() { return this.introTarget ?? this.introExpanded; },
    collectionMode() { return this.isMobileCollection ? 'index' : this.viewMode; },
    searchTerms() {
      return this.searchQuery
        .toLowerCase()
        .split(/[\s,.;:!?()[\]{}"']+/)
        .map((term) => term.trim())
        .filter((term) => term.length >= 2);
    },
    searchActive() {
      return this.searchTerms.length > 0;
    },
    routeTargetId() {
      const hash = this.$route && this.$route.hash ? this.$route.hash.replace(/^#/, '') : '';
      const raw = hash || (this.$route && this.$route.query ? this.$route.query.to : '') || '';
      if (!raw) return '';
      try {
        return decodeURIComponent(raw);
      } catch (error) {
        return raw;
      }
    },
    hasSearchMatches() {
      return researchPublications.some((entry) => this.entryMatches(entry.id));
    },
    visiblePublications() {
      return researchPublications.filter((entry) => this.entryMatches(entry.id));
    },
    compactEntries() {
      return this.visiblePublications.map((entry) => ({
        id: entry.id,
        anchor: entry.anchor || entry.id,
        title: this.pubTitle(entry),
        subtitle: this.pubSubtitle(entry),
        preview: entry.preview,
        body: this.pubDesc[entry.id],
        category: this.groupLabel(entry.group),
        period: entry.period,
        status: entry.status,
        links: entry.links || [],
        publication: entry,
        images: (entry.images || []).map((image, index) => ({
          src: this.imageThumb(image),
          fullSrc: this.imageFull(image),
          alt: `${entry.title}, ${entry.figureLabels?.[index] || `figure ${index + 1}`}`,
          label: entry.figureLabels?.[index] || `Figure ${index + 1}`,
          caption: entry.figureCaptions?.[index] || ''
        })),
        lab: entry.lab,
        sourceId: `research:${entry.id}`,
        kind: 'research'
      }));
    },
    menuChildren() {
      const out = {};
      for (const item of this.chapterItems) {
        out[item.key] = researchPublications
          .filter((entry) => entry.group === item.key && this.entryMatches(entry.id))
          .map((entry) => ({
            id: entry.anchor || entry.id,
            label: this.pubTitle(entry),
            subtitle: this.pubSubtitle(entry) || '',
            date: entry.period,
          }));
      }
      return out;
    },
    chapterItems() {
      return [
        { key: 'humanAi', id: 'research-human-ai', label: 'Human × AI', title: 'Human-AI interaction and collaboration' },
        { key: 'human', id: 'research-human', label: 'Human learning', title: 'Human learning and computational cognition' },
        { key: 'lm', id: 'research-models', label: 'AI models', title: 'Language models, behaviour, and interpretability' }
      ].filter((item) => this.visiblePublications.some((entry) => entry.group === item.key));
    }
  },
  watch: {
    '$route.hash'() {
      this.handleRouteTargetArrival();
    },
    '$route.query.to'() {
      this.handleRouteTargetArrival();
    },
    searchQuery() {
      this.refreshScrollReveal();
    }
  },
  mounted() {
    try {
      const saved = window.localStorage.getItem('research-view-mode');
      if (['grid', 'index'].includes(saved)) this.viewMode = saved;
    } catch (error) {
      // Browsing preferences remain optional when storage is unavailable.
    }
    this.setupIntroViewport();
    this.setupCollectionViewport();
    this.handleRouteTargetArrival();
  },
  beforeUnmount() {
    this.cancelIntroMotion();
    if (this._introMedia && this._onIntroViewport) {
      this._introMedia.removeEventListener('change', this._onIntroViewport);
    }
  },
  unmounted() {
    this.teardownCollectionViewport();
  },
  methods: {
    setupIntroViewport() {
      this._introMedia = window.matchMedia('(min-width: 1080px)');
      this._onIntroViewport = (event) => {
        this.cancelIntroMotion();
        this.wideIntro = event.matches;
      };
      this._onIntroViewport(this._introMedia);
      this._introMedia.addEventListener('change', this._onIntroViewport);
    },
    cancelIntroMotion() {
      this._introMotionToken = (this._introMotionToken || 0) + 1;
      window.cancelAnimationFrame(this._introScrollFrame);
      (this._introAnimations || []).forEach(animation => animation.cancel());
      this._introAnimations = [];
      this.introTarget = null;
    },
    handleRouteTargetArrival() {
      this.pendingRouteTargetId = this.routeTargetId;
    },
    clearPendingRouteTarget(targetId) {
      if (this.pendingRouteTargetId === targetId) this.pendingRouteTargetId = '';
    },
    setupCollectionViewport() {
      this._collectionMobileMedia = window.matchMedia('(max-width: 720px)');
      this._handleCollectionViewport = (event) => {
        this.isMobileCollection = event.matches;
      };
      this._handleCollectionViewport(this._collectionMobileMedia);
      if (this._collectionMobileMedia.addEventListener) {
        this._collectionMobileMedia.addEventListener('change', this._handleCollectionViewport);
      } else {
        this._collectionMobileMedia.addListener(this._handleCollectionViewport);
      }
    },
    teardownCollectionViewport() {
      if (!this._collectionMobileMedia || !this._handleCollectionViewport) return;
      if (this._collectionMobileMedia.removeEventListener) {
        this._collectionMobileMedia.removeEventListener('change', this._handleCollectionViewport);
      } else {
        this._collectionMobileMedia.removeListener(this._handleCollectionViewport);
      }
    },
    pubTitle(pub) {
      const title = String(pub.title);
      const colon = title.match(/[:\uff1a]/);
      if (colon) return title.slice(0, colon.index).trim();
      const q = title.match(/\?/);
      return q ? title.slice(0, q.index + 1).trim() : title;
    },
    pubSubtitle(pub) {
      const title = String(pub.title);
      if (pub.subtitle) return pub.subtitle;
      const colon = title.match(/[:\uff1a]/);
      if (colon) return title.slice(colon.index + 1).trim();
      const q = title.match(/\?/);
      return q ? title.slice(q.index + 1).trim() : '';
    },
    stripHtml(text) {
      return String(text || '').replace(/<[^>]+>/g, ' ');
    },
    hlText(text) {
      return highlightText(text, this.searchTerms);
    },
    hlHtml(html) {
      return highlightHtml(html, this.searchTerms);
    },
    imageThumb(name) {
      return name.startsWith('/') ? name : `/assets/img/gallery-thumbs/${name}.png.webp`;
    },
    imageFull(name) {
      return name.startsWith('/') ? name : `/assets/img/${name}.png`;
    },
    entryById(id) {
      return researchPublications.find((entry) => entry.id === id);
    },
    entrySearchText(entry) {
      return [
        entry.title,
        entry.text,
        this.stripHtml(this.pubDesc[entry.id])
      ].join(' ').toLowerCase();
    },
    entryMatches(id) {
      const entry = this.entryById(id);
      if (!entry) return false;
      const text = this.entrySearchText(entry);
      return this.searchTerms.every((term) => text.includes(term));
    },
    groupLabel(groupKey) {
      const item = this.chapterItems.find((entry) => entry.key === groupKey);
      return item ? item.label : groupKey;
    },
    chapterId(groupKey) {
      const item = this.chapterItems.find((entry) => entry.key === groupKey);
      return item ? item.id : '';
    },
    isGroupStart(entry, index) {
      return this.visiblePublications
        .findIndex((pub) => pub.group === entry.group) === index;
    },
    async collapseIntro({ intro, movingElements, positions, outgoing, motionToken }) {
      const scrollStart = window.scrollY;
      const expandedHeight = intro.getBoundingClientRect().height;
      // Measure the destination without painting the collapsed state. Keep the
      // expanded document in place until both layout and scrolling have finished.
      this.introExpanded = false;
      await this.$nextTick();
      if (motionToken !== this._introMotionToken) return;
      const targets = movingElements.map(element => element.getBoundingClientRect().top + window.scrollY);
      const collapsedHeight = intro.getBoundingClientRect().height;
      this.introExpanded = true;
      await this.$nextTick();
      if (motionToken !== this._introMotionToken) return;
      window.scrollTo({ top: scrollStart, behavior: 'instant' });

      const timing = { duration: 280, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'both' };
      movingElements.forEach((element, index) => {
        const base = element.getBoundingClientRect().top + window.scrollY;
        this._introAnimations.push(element.animate([
          { transform: `translateY(${positions[index] - base}px)` },
          { transform: `translateY(${targets[index] - base}px)` }
        ], timing));
      });
      this._introAnimations.push(intro.animate([
        { clipPath: 'inset(0)' },
        { clipPath: `inset(0 0 ${Math.max(0, expandedHeight - collapsedHeight)}px 0)` }
      ], timing));
      outgoing.forEach(({ element, opacity }) => {
        this._introAnimations.push(element.animate([
          { opacity }, { opacity: 0 }
        ], { duration: 80, easing: 'ease-out', fill: 'both' }));
      });

      // The scroll follows the same eased progress as the layout, so their
      // opposing screen movements cannot drift apart or reverse direction.
      const driver = this._introAnimations[0];
      this._introAnimations.forEach(animation => {
        animation.pause();
        animation.currentTime = 0;
      });
      const startTime = performance.now();
      const scrollFrame = (now) => {
        if (motionToken !== this._introMotionToken) return;
        const elapsed = Math.max(0, Math.min(now - startTime, timing.duration));
        this._introAnimations.forEach(animation => {
          animation.currentTime = Math.min(elapsed, animation.effect.getTiming().duration);
        });
        const progress = driver.effect.getComputedTiming().progress ?? 0;
        window.scrollTo({ top: scrollStart * (1 - progress), behavior: 'instant' });
        if (elapsed < timing.duration) {
          this._introScrollFrame = window.requestAnimationFrame(scrollFrame);
        } else {
          this._introAnimations.forEach(animation => animation.finish());
        }
      };
      scrollFrame(startTime);
      await Promise.all(this._introAnimations.map(animation => animation.finished.catch(() => {})));
      if (motionToken !== this._introMotionToken) return;
      window.cancelAnimationFrame(this._introScrollFrame);
      window.scrollTo({ top: 0, behavior: 'instant' });
      this.introExpanded = false;
      await this.$nextTick();
      if (motionToken !== this._introMotionToken) return;
      this._introAnimations.forEach(animation => animation.cancel());
      this._introAnimations = [];
      this.introTarget = null;
    },
    async toggleIntro(event) {
      const intro = this.$refs.researchIntro;
      const collection = this.$refs.researchCollection;
      const button = intro.querySelector('.research-more');
      const illustration = intro.querySelector('.research-hero-crop');
      const nextExpanded = !this.introControlExpanded;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const animate = event?.detail !== 0 && !reduceMotion;
      const movingElements = [button, illustration, collection];
      const positions = movingElements.map(element => element.getBoundingClientRect().top + window.scrollY);
      const textElements = () => this.introExpanded
        ? [...intro.querySelectorAll('.research-detail-column')]
        : [];
      const outgoing = textElements().map(element => ({
        element,
        opacity: getComputedStyle(element).opacity
      }));
      this.cancelIntroMotion();
      const motionToken = this._introMotionToken;
      this.introTarget = nextExpanded;

      if (animate && !nextExpanded && this.introExpanded) {
        await this.collapseIntro({ intro, movingElements, positions, outgoing, motionToken });
        return;
      }

      if (animate) {
        // Hold an interrupted layout at its current visual position while the
        // outgoing words fade. A second click can reverse the requested state.
        movingElements.forEach((element, index) => {
          const offset = positions[index] - (element.getBoundingClientRect().top + window.scrollY);
          if (Math.abs(offset) < 0.5) return;
          const hold = element.animate([
            { transform: `translateY(${offset}px)` },
            { transform: `translateY(${offset}px)` }
          ], { duration: 1, fill: 'both' });
          hold.pause();
          this._introAnimations.push(hold);
        });
        const fadeOut = outgoing.map(({ element, opacity }) => element.animate([
          { opacity }, { opacity: 0 }
        ], { duration: 80, easing: 'ease-out', fill: 'forwards' }));
        this._introAnimations.push(...fadeOut);
        await Promise.all(fadeOut.map(animation => animation.finished.catch(() => {})));
        if (motionToken !== this._introMotionToken) return;
      }

      const before = movingElements.map(element => element.getBoundingClientRect().top);
      this._introAnimations.forEach(animation => animation.cancel());
      this._introAnimations = [];
      this.introExpanded = nextExpanded;
      await this.$nextTick();
      if (motionToken !== this._introMotionToken) return;

      if (animate) {
        const duration = nextExpanded ? 240 : 220;
        const easing = 'cubic-bezier(0.23, 1, 0.32, 1)';
        movingElements.forEach((element, index) => {
          const offset = before[index] - element.getBoundingClientRect().top;
          if (Math.abs(offset) < 0.5) return;
          this._introAnimations.push(element.animate([
            { transform: `translateY(${offset}px)` },
            { transform: 'translateY(0)' }
          ], { duration, easing }));
        });
        textElements().forEach(element => this._introAnimations.push(element.animate([
          { opacity: 0 }, { opacity: getComputedStyle(element).opacity }
        ], { duration: 180, easing: 'ease-out' })));
        if (nextExpanded) {
          intro.querySelectorAll('.research-detail-column').forEach(element => {
            this._introAnimations.push(element.animate([
              { clipPath: 'inset(0 0 100% 0)' },
              { clipPath: 'inset(0)' }
            ], { duration, easing }));
          });
        }
      }

      if (!nextExpanded) {
        window.scrollTo({
          top: 0,
          behavior: animate ? 'smooth' : 'instant',
        });
      }
      await Promise.all(this._introAnimations.map(animation => animation.finished.catch(() => {})));
      if (motionToken === this._introMotionToken) {
        this._introAnimations = [];
        this.introTarget = null;
      }
    },
    async setViewMode(mode) {
      if (this.isMobileCollection || !['grid', 'index'].includes(mode) || mode === this.viewMode) return;
      try {
        window.localStorage.setItem('research-view-mode', mode);
      } catch (error) {
        // The selected view still works for this visit without persistence.
      }
      const applyMode = async () => {
        this.viewMode = mode;
        await this.$nextTick();
        this.refreshScrollReveal();
      };
      const reduceMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!document.startViewTransition || reduceMotion) {
        applyMode();
        return;
      }
      const releaseCursorLock = acquireInteractiveCursorLock();
      this.viewTransitionActive = true;
      const transitionToken = (this._viewTransitionToken || 0) + 1;
      this._viewTransitionToken = transitionToken;
      await this.$nextTick();
      let transition;
      try {
        transition = document.startViewTransition(applyMode);
      } catch (error) {
        releaseCursorLock();
        this.viewTransitionActive = false;
        await applyMode();
        return;
      }
      transition.finished.catch(() => {}).finally(() => {
        releaseCursorLock();
        if (this._viewTransitionToken === transitionToken) this.viewTransitionActive = false;
      });
    },
    // Toggle disabled: groups stay expanded.
    // toggleGroup(groupKey) {
    //   if (this.searchActive) return;
    //   this.expanded = {
    //     ...this.expanded,
    //     [groupKey]: !this.expanded[groupKey]
    //   };
    //   this.refreshScrollReveal();
    // },
    clearSearch() {
      this.searchQuery = '';
    }
  }
}
</script>

<style scoped>
    @import url('../assets/css/list.css');

    .research-page {
      overflow-anchor: none;
    }

    .research-heading {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 2rem;
    }

    .research-title {
      margin-top: 0;
      scroll-margin-top: 1.35rem;
    }

    .research-summary > p,
    .research-detail-column > p:first-child {
      margin-top: 0;
    }

    .research-hero-crop {
      display: block;
      margin: 1.4rem auto 0;
      width: 100%;
      max-width: none;
      aspect-ratio: 7 / 3;
      overflow: hidden;
    }

    .research-hero-image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center 57%;
      border-radius: 0;
    }

    @media (min-width: 1080px) {
      .research-heading {
        margin-bottom: 1.8rem;
      }

      .research-title {
        margin-bottom: 0;
      }

      .research-intro {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: auto 1fr auto;
        column-gap: 3.5rem;
        align-items: start;
        margin-bottom: 2.8rem;
      }

      .research-summary {
        grid-column: 1;
        grid-row: 1;
      }

      .research-summary > p {
        margin-bottom: 0;
      }

      .research-summary-lead,
      .research-summary-rest {
        display: block;
        font-size: 1.0625rem;
        line-height: 1.65;
        text-wrap: pretty;
      }

      .research-summary-rest {
        margin-top: 1.2rem;
      }

      .research-intro.is-expanded .research-details {
        display: contents;
      }

      .research-detail-left {
        grid-column: 1;
        grid-row: 2;
        padding-top: 1.5rem;
      }

      .research-detail-right {
        grid-column: 2;
        grid-row: 2;
        padding-top: 1.5rem;
      }

      .research-hero-crop {
        grid-column: 2;
        grid-row: 1;
        margin: 0;
      }

      .research-detail-column > p {
        margin: 0 0 1.5rem;
        font-size: 1.0625rem;
        line-height: 1.65;
      }

      .research-detail-column > p:last-child {
        margin-bottom: 0;
      }

      .research-more {
        grid-column: 1;
        grid-row: 3;
        justify-self: start;
        margin-top: 1.1rem;
      }
    }

    div.scroll-container.project.proof-gallery {
      --proof-gallery-height: clamp(210px, 34vh, 300px);
      height: auto;
      align-items: center;
    }

    div.scroll-container.project.proof-gallery img {
      height: auto;
      max-height: calc(var(--proof-gallery-height) - 20px);
      padding: 0;
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 0;
      box-shadow: none;
      background: #fff;
    }


    .collection-tools {
      justify-content: space-between;
    }

    @media (max-width: 720px) {
      .research-hero-crop {
        aspect-ratio: 9 / 4;
      }

      .collection-tools {
        display: flex;
        width: 100%;
      }

      .collection-tools .timeline-search {
        display: none;
      }
    }

    .research-more {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      flex-shrink: 0;
      margin: 0.15rem 0 0.35rem;
      min-height: 36px;
      padding: 0.5rem 0;
      border: 0;
      background: transparent;
      color: var(--site-text-secondary);
      font-family: "Quattrocento Sans", sans-serif;
      font-size: 0.8125rem;
      font-weight: 400;
      line-height: 1.4;
      cursor: pointer;
      transition: color 160ms ease, transform 120ms ease;
    }

    .research-more > span {
      text-decoration: none;
    }

    .research-more :deep(svg) {
      width: 0.75rem;
      height: 0.75rem;
      opacity: 0.55;
      transition: opacity 160ms ease, transform 160ms ease;
    }

    .research-more:focus-visible {
      outline: 1px solid currentColor;
      outline-offset: 5px;
    }

    @media (hover: hover) and (pointer: fine) {
      .research-more:hover {
        color: inherit;
      }

      .research-more:hover :deep(svg) {
        opacity: 0.9;
        transform: scale(1.1);
      }

      .research-more:active {
        transform: translateY(1px);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .research-more,
      .research-more :deep(svg) {
        transition: none;
      }
    }

    .timeline-event-copy :deep(mark) {
      background: rgba(255, 222, 120, 0.45);
      color: inherit;
      border-radius: 2px;
      padding: 0 1px;
    }

    :global(body.dark-mode .timeline-event-copy mark) {
      background: rgba(255, 210, 90, 0.3);
    }

</style>
